import * as React from "react";
import { filterByCategory, groupByGroupName, sortFilteredData } from "..";
import { GroupBy, SortBy } from "../types";
import { getPersisted } from "../../../../utils/storage";
import { IndexByNumber } from "../../../../redux/modules/insights/utils";
import { GROUP_BY_KEY, SORT_BY_KEY, MONITORING_LS_KEY } from "../constants";
import { useCounts } from "./useCounts";
import { Category } from "../../utils/types";

type Composed = Insite.Insights.ComposedOccurrence[];
type Insights = Insite.Insights.ComposedInsight[][];

export const useMonitoringState = (
  data: Insite.AppState["monitoring"],
  category: Category,
  showSnoozed: boolean,
  filterFirst = false
) => {
  const [group, setGroup] = React.useState<GroupBy>(
    getPersisted(
      { prop: GROUP_BY_KEY, key: MONITORING_LS_KEY },
      GroupBy.Priority
    ) || GroupBy.Priority
  );
  const [sort, setSort] = React.useState<SortBy>(
    getPersisted(
      { prop: SORT_BY_KEY, key: MONITORING_LS_KEY },
      SortBy.AlphaAsc
    ) || SortBy.AlphaAsc
  );
  const [preFiltered, setPreFiltered] = React.useState<Composed>([]);
  const [filtered, setFiltered] = React.useState<Composed>([]);
  const [composed, setComposed] = React.useState<Composed>([]);
  const [grouped, setGrouped] = React.useState<Composed[]>([]);
  const [composedGrouped, setComposedGrouped] = React.useState<Insights>([]);

  type FinalGrouped = { composed: Insights; group: GroupBy };
  const [finalGrouped, setFinalGrouped] = React.useState<FinalGrouped>({
    composed: [],
    group:
      getPersisted(
        { prop: GROUP_BY_KEY, key: MONITORING_LS_KEY },
        GroupBy.Priority
      ) || GroupBy.Priority,
  });

  const counts = useCounts(group, grouped, composedGrouped, showSnoozed!);

  // Process initial data
  React.useLayoutEffect(() => {
    if (data.composed.length > 0) {
      setComposed(data.composed);
      setPreFiltered(data.composed);
    }
  }, [data.composed.length]);

  // Replace hits with composed insights on data
  React.useLayoutEffect(() => {
    const filt = filtered.length > 0 ? filtered : preFiltered;
    if (filt.length === 0) return;
    const insights = filt.reduce(
      (mapped: IndexByNumber<Insite.Insights.ComposedOccurrence>, occ) => {
        mapped[occ.occurrenceId] = occ;
        return mapped;
      },
      {}
    );

    const newData = {
      ...data,
      hit: insights,
    };
    // @ts-ignore
    const filteredByCategory = filterByCategory(category, group, newData);
    setGrouped(filteredByCategory);
  }, [category, group, filtered, composed, sort]);

  // Compose final insight groups
  React.useEffect(() => {
    const composedGroups = grouped.map((occ) => groupByGroupName(group, occ));
    const sortedComposedGroups = sortFilteredData(composedGroups, group, sort);
    setComposedGrouped(sortedComposedGroups);
  }, [grouped, sort]);

  React.useEffect(() => {
    if (filterFirst && filtered.length > 0) {
      setFinalGrouped({ composed: composedGrouped, group });
    }

    if (!filterFirst) {
      setFinalGrouped({ composed: composedGrouped, group });
    }
  }, [composedGrouped]);

  return {
    composed,
    finalGrouped,
    counts,
    filtered,
    setFiltered,
    setGroup,
    setSort,
    sort,
  };
};
